<script setup lang="ts">
  import useScreenModes from '@/composables/useScreenModes';
  import {
    computed,
    onMounted,
    ref,
    toRefs,
    useTemplateRef,
    watch,
  } from 'vue';
  import { uniqId } from '@/utils/helpers';
  import { useI18n } from 'vue-i18n';
  import ElButton from '@/components/common/formElements/buttons/ElButton.vue';
  import { useElementSize } from '@vueuse/core';
  import type {
    MarkdownBinding, MarkdownBindingAttrs,
  } from '@/directives/v-markdown';
  import vMarkdown from '@/directives/v-markdown';

  defineOptions({ name: 'CollapsableText' });

  const { ellipsisVisibleLines = 3, mobileCollapsable = true, desktopCollapsable = false, ...props } = defineProps<{
    text: string;
    ellipsisVisibleLines?: number;
    mobileCollapsable?: boolean;
    desktopCollapsable?: boolean;
    linkClass?: string;
    /**
     * Binding optionnel à passer en attribut à la directive v-markdown pour spécifier les classes à ajouter sur
     * les éléments HTML ainsi que la liste des tags supportée par l'instance, s'il y a lieu.
     * */
    markdownBinding?: MarkdownBindingAttrs;
  }>();

  const { mobile } = toRefs(useScreenModes());
  const messageRef = useTemplateRef<HTMLParagraphElement>('messageRef');
  const messageClampRef = useTemplateRef<HTMLParagraphElement>('messageClampRef');
  const controlId = ref<string>(uniqId('ellipsis-'));
  const hasEllipsis = ref<boolean>(false);
  const isExpanded = ref<boolean>(mobile.value ? !mobileCollapsable : !desktopCollapsable);
  const { t } = useI18n();

  const isCollapsable = computed<boolean>(() => {
    return mobile.value ? mobileCollapsable && hasEllipsis.value : desktopCollapsable && hasEllipsis.value;
  });

  const markdownAttr = computed<MarkdownBinding>(() => {
    if (props.markdownBinding) {
      return { text: props.text, ...props.markdownBinding };
    }
    return props.text;
  });

  const { width: messageWidth } = useElementSize(messageRef, undefined, { box: 'border-box' });

  watch(mobile, () => {
    if (!(mobileCollapsable && desktopCollapsable)) {
      isExpanded.value = !((mobile.value ? mobileCollapsable : desktopCollapsable) && hasEllipsis.value);
    }
  });

  const updateClampRefWidth = () => {
    if (messageRef.value && messageClampRef.value) {
      messageClampRef.value.style.width = window.getComputedStyle(messageRef.value).width;
      hasEllipsis.value = messageClampRef.value.clientHeight < messageClampRef.value.scrollHeight;
    }
  };

  watch(hasEllipsis, () => isExpanded.value = !hasEllipsis.value);

  onMounted(() => {
    updateClampRefWidth();
  });

  watch(messageWidth, () => {
    updateClampRefWidth();
  });
</script>

<template>
  <div class="space-y-3">
    <div
      :id="controlId"
      ref="messageRef"
      v-markdown="markdownAttr"
      aria-live="polite"
      :class="{'line-clamp-(--ellipsis-visible-lines,3)': isCollapsable && !isExpanded}"
      :style="`--ellipsis-visible-lines:${ellipsisVisibleLines}`"
    />
    <div
      ref="messageClampRef"
      v-markdown="markdownAttr"
      class="invisible absolute line-clamp-(--ellipsis-visible-lines,3)"
      :style="`--ellipsis-visible-lines:${ellipsisVisibleLines}`"
      inert
    />
    <ElButton
      v-if="isCollapsable"
      :class="linkClass || 'font-bold text-inherit!'"
      link
      :aria-expanded="isExpanded"
      :aria-controls="controlId"
      :aria-label="t(isExpanded ? 'commonComponents.collapsableText.ariaShowLess' : 'commonComponents.collapsableText.ariaShowMore')"
      @click="isExpanded = !isExpanded"
    >
      {{ t(isExpanded ? 'commonComponents.collapsableText.showLess' : 'commonComponents.collapsableText.showMore') }}
    </ElButton>
  </div>
</template>
